// ── Login Page ──
//
// Panes off local `mode`: signin (email+password + Google), signup (the
// verified-email wizard, C1496), twofa (6-digit code, second step of a 2FA
// login), forgot (request a password-reset link, TPT194 — the link's
// redemption page is pages/reset-password.js). `tempToken` is held in a
// module-local var only — never localStorage,
// it's a 5-minute-TTL token the server hard-rejects on any protected route
// (C1128). The wizard's `signupToken` follows the identical rule — see its
// declaration below. `loginCaptcha` (TPT268, the login captcha challenge)
// follows the same in-memory-only rule — see its declaration below.
//
// Desktop sign-in handoff (C1249): the Task App (Electron) opens this page in
// the system browser at `#/login?d=<base64url {port,nonce}>` instead of going
// straight to Google itself. `d` is decoded into module-local `desktopHandoff`
// and mirrored to sessionStorage (so a reload mid-flow — e.g. during the 2FA
// step or the signup wizard — doesn't strand the user back on a plain sign-in
// screen). Any successful sign-in/sign-up/2FA in that mode redirects the
// browser to the Task App's loopback callback
// (`http://127.0.0.1:<port>/callback?token=…`) instead of navigating into the
// SPA. The Google branch carries `d` through as `?desktop=` on
// `/api/auth/google/login`, which threads it through Google's OAuth `state`
// and back out the other side — see `api/src/routes/auth.js`'s
// `parseDesktopState()`/`desktop` branch.
//
// Account chooser (TPT347): Project ▸ Re-authenticate / Change Account opens the same URL
// with `&choose_account=1`. If this browser already has a live session, the page then shows a
// `choose` pane — "Continue as <account>" (hands the session back, as before) vs. "Use a
// different account" (signs this browser out, then the normal sign-in form; the Google button
// asks Google for its own account picker) — instead of silently handing the session back, which
// made switching accounts impossible without first signing out on the web. Without
// `choose_account` behavior is unchanged. Parsing/serializing lives in lib/desktop-handoff.js.
//
// Open signup entry (TPT533): `#/signup` (signupPage() below) opens this page straight on the
// signup wizard's email step with no beta code — the marketing site's release CTA target, and
// where pages/register.js sends a code-less visitor in `release`. It queues the same pendingBeta
// handoff startBetaSignup() uses; the server's beta gate still decides whether signup is open.

import { verifySession, getUser, signOutSession, loginWithPassword, completeTwoFactorLogin, signupStart, signupVerify, signupComplete, forgotPassword } from '../auth.js';
import { getToken, apiFetch } from '../api.js';
import { navigate } from '../router.js';
import { teardownLayout } from '../layout.js';
import { showToast, esc } from '../ui.js';
import { renderAvatarHtml } from '../utils.js';
import { EXPERIENCE_OPTIONS, signupErrorText, betaRequiredNotice, forgotPasswordErrorText, captchaChallengeFromError } from '../lib/signup-form.js';
import { bindAvatarPicker } from '../lib/avatar-pick.js';
import { passwordToggleButton, bindPasswordToggle } from '../lib/password-toggle.js';
import { brandLockup } from '../lib/brand.js';
import { readHandoffFromHash, googleLoginHref } from '../lib/desktop-handoff.js';

const HANDOFF_STORAGE_KEY = 'tt_desktop_handoff';

let mode = 'signin';
let tempToken = null;
let pendingEmail = '';
let desktopHandoff = null;
// The already-signed-in account the `choose` pane offers to continue as (getUser()), else null.
let chooserUser = null;

// ── Login captcha (TPT268) ──
// `{ question, token }` from captchaChallengeFromError(), or null. Set only
// when POST /auth/login answers `403 { captcha_required }` (the server arms the
// gate per IP after repeated failures — api/src/lib/login-captcha.js). The
// token is a one-shot, 5-minute signed challenge: module-local only, like
// `tempToken`, never localStorage/sessionStorage. Every 403 hands back a fresh
// challenge (the one just sent is spent), so it is replaced on each 403 and
// dropped on success, on any other error (the server consumed it even if the
// password was then wrong), and on every pane change.
let loginCaptcha = null;

// ── Forgot-password pane state (TPT194) ──
// forgotEmail: the address typed into the forgot pane — seeded from the
// sign-in email field when the user clicks "Forgot password?", and echoed back
// on the confirmation. forgotSent: flips the pane from the request form to the
// generic "check your inbox" state. The server answers the same 200 for a real
// account, an unknown address, its invisible 60s cooldown, and even a mail
// failure (no account enumeration), so this can only ever confirm "if an
// account exists" — never "we sent it".
// pendingForgot: set by pages/reset-password.js's startPasswordReset() export
// before it navigate()s here (dead-link "Request a new link"); applied once in
// loginPage()'s tail, same shape as pendingBeta below.
let forgotEmail = '';
let forgotSent = false;
let pendingForgot = false;

// ── Signup wizard state (C1496) ──
// signupStep: which of the wizard's three sub-panes to show while
// mode === 'signup'. signupStep only ever advances to 'profile' via a
// successful signupVerify() call (see bind()'s #signup-code-form handler) —
// that assignment, not a UI check, is what makes "profile is unreachable
// until the email is verified" true.
let signupStep = 'email';   // 'email' | 'code' | 'profile'
let signupEmail = '';
// The signup token is a stateless 30-minute JWT ({email, signup_pending:true})
// that requireAuth hard-rejects on every protected route — same shape and
// same rule as `tempToken` above (the 2FA temp_token): it authenticates
// nothing on its own, so it must live in memory only, NEVER
// localStorage/sessionStorage. Unlike `desktopHandoff` below (which is
// deliberately mirrored to sessionStorage so a reload survives), a reload
// mid-wizard is meant to drop back to a fresh signin pane.
let signupToken = null;
let signupAvatar = null;    // data: URL from fileToAvatarDataUrl(), or null
let resendReadyAt = 0;      // epoch ms the Resend button re-enables (0 = never sent)
let resendTimerId = null;

// ── Beta registration handoff (TPT96) ──
// betaCode: threaded through signupStart()/signupComplete() so the server
// can re-check + eventually consume the beta_signups row it came from.
// Blank is fine — it just means "no code, let the server's invite/beta-mode
// checks decide" (an invited user, or beta mode off).
// pendingBeta: set by pages/register.js's startBetaSignup() export before it
// navigate()s here — applied once, in loginPage()'s tail below, then
// cleared. Module state survives the hash navigation because it's the same
// JS context (no full page reload), so there is nothing to persist to
// sessionStorage for this handoff, unlike invite.js's PENDING_INVITE_KEY.
let betaCode = '';
let pendingBeta = null;

function resetSignupState() {
  signupStep = 'email';
  signupEmail = '';
  signupToken = null;
  signupAvatar = null;
  betaCode = '';
  resendReadyAt = 0;
  stopResendTimer();
}

// Called by pages/register.js once it has validated a beta code against
// POST /auth/beta/validate. Queues the wizard to open pre-seeded with the
// discovered email and code the next time loginPage() runs — the caller
// navigates('/login') right after this.
export function startBetaSignup({ code, email }) {
  pendingBeta = { code, email };
}

// Called by pages/reset-password.js's dead-link state ("Request a new link").
// Queues the forgot pane to open the next time loginPage() runs — the caller
// navigate('/login')s right after this. Same one-shot module-state handoff as
// startBetaSignup() above.
export function startPasswordReset() {
  pendingForgot = true;
}

// `#/signup` route handler (TPT533): the signup wizard's email step, no code, no
// pre-filled email — startBetaSignup()'s pending-state hook with both blank.
export function signupPage() {
  startBetaSignup({ code: '', email: '' });
  return loginPage();
}

// Paints a signup-step error into `errEl`. A 403 beta_required rejection gets the
// localized notice plus a link to the marketing site's /join page; everything
// else is signupErrorText()'s plain inline string, as before.
function showSignupError(errEl, err, step) {
  const notice = betaRequiredNotice(err, typeof navigator !== 'undefined' ? navigator.language : '');
  if (notice) {
    errEl.innerHTML = `${esc(notice.message)} <a href="${esc(notice.href)}">${esc(notice.linkText)}</a>`;
  } else {
    errEl.textContent = signupErrorText(err, step);
  }
}

function stopResendTimer() {
  if (resendTimerId) { clearInterval(resendTimerId); resendTimerId = null; }
}

// Ticks the Resend button's countdown. Self-healing: router.js never invokes
// a page's cleanup return value (see Gotchas in tt-web-auth.md), so if the
// user has navigated away from the code pane while this interval is still
// running, `#signup-resend-btn` is simply gone from the DOM — treat that as
// the signal to stop, rather than relying on every navigation path to
// remember to call stopResendTimer() itself.
function updateResendButton() {
  const btn = document.getElementById('signup-resend-btn');
  if (!btn) { stopResendTimer(); return; }
  const secondsLeft = Math.ceil((resendReadyAt - Date.now()) / 1000);
  if (secondsLeft > 0) {
    btn.disabled = true;
    btn.textContent = `Resend code (${secondsLeft}s)`;
  } else {
    btn.disabled = false;
    btn.textContent = 'Resend code';
    stopResendTimer();
  }
}

function startResendTimer() {
  stopResendTimer();
  updateResendButton();
  resendTimerId = setInterval(updateResendButton, 1000);
}

// ── Desktop handoff helpers ──

function readStoredHandoff() {
  try {
    const raw = sessionStorage.getItem(HANDOFF_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Redirect the browser to the Task App's loopback callback with the freshly
// issued token. Called instead of navigate('/') on any successful sign-in/up
// (including the signup wizard's final step) while desktopHandoff is set.
// `explicitToken` — the cookie→Bearer bridge token from GET /auth/session-token
// (C1466) — is only passed by the cookie-only-session shortcut in loginPage();
// every other caller omits it and falls back to getToken() (already
// populated via setAuthToken()). This is a full-page navigation out of the
// SPA — nothing may run after calling it.
function completeHandoff(explicitToken) {
  const { port, nonce } = desktopHandoff;
  sessionStorage.removeItem(HANDOFF_STORAGE_KEY);
  desktopHandoff = null;
  const token = explicitToken || getToken();
  window.location.href =
    `http://127.0.0.1:${port}/callback?token=${encodeURIComponent(token)}&nonce=${encodeURIComponent(nonce)}`;
}

function render() {
  const app = document.getElementById('app');
  app.innerHTML = `
    <div class="login-container">
      ${brandLockup()}
      <div class="auth-card">
        ${mode === 'signin' ? renderSignin()
          : mode === 'choose' ? renderChoose()
          : mode === 'signup' ? renderSignupWizard()
          : mode === 'forgot' ? renderForgot()
          : renderTwofa()}
      </div>
    </div>`;
  bind();
}

// Account-chooser pane (TPT347) — only reachable in a desktop handoff started with
// `choose_account=1` while this browser already has a live session.
function renderChoose() {
  const u = chooserUser || {};
  const label = u.name || u.email || 'this account';
  return `
    <p>Signing in to the TipΔTask desktop app</p>
    <div class="login-choose-account">
      ${renderAvatarHtml(u.id || u.email ? u : null, 40)}
      <div class="login-choose-who">
        <strong>${esc(u.name || u.email || '')}</strong>
        ${u.name && u.email ? `<span>${esc(u.email)}</span>` : ''}
      </div>
    </div>
    <p>You’re signed in to Tipatask in this browser. Continue with this account, or use a different one?</p>
    <div class="login-error" id="choose-error"></div>
    <button type="button" class="btn btn-primary btn-block" id="choose-continue">Continue as ${esc(label)}</button>
    <button type="button" class="btn btn-ghost btn-block" id="choose-switch">Use a different account</button>`;
}

function renderSignin() {
  const googleHref = googleLoginHref(desktopHandoff);
  return `
    ${desktopHandoff ? '<p>Signing in to the TipΔTask desktop app</p>' : ''}
    <form class="login-form" id="signin-form">
      <div class="form-group">
        <label class="form-label" for="signin-email">Email</label>
        <input class="form-input" id="signin-email" type="email" autocomplete="email" required>
      </div>
      <div class="form-group">
        <label class="form-label" for="signin-password">Password</label>
        <input class="form-input" id="signin-password" type="password" autocomplete="current-password" required>
      </div>
      <div class="login-forgot"><a href="#" id="go-forgot">Forgot password?</a></div>
      <div class="login-error" id="signin-error"></div>
      <div id="login-captcha-slot">${renderLoginCaptcha()}</div>
      <button type="submit" class="btn btn-primary btn-block" id="signin-btn">Sign In</button>
    </form>
    <div class="login-divider"><span>or</span></div>
    <a href="${googleHref}" class="btn btn-ghost login-google-btn">Sign in with Google</a>
    <div class="login-switch">No account? <a href="#" id="go-signup">Create account</a></div>`;
}

// The captcha field, or '' when no challenge is pending. Painted into
// `#login-captcha-slot` on its own (see showLoginCaptcha()) so revealing it
// never re-renders the pane and wipes the typed email/password.
function renderLoginCaptcha() {
  if (!loginCaptcha) return '';
  return `
    <div class="form-group">
      <label class="form-label" for="login-captcha">What is ${esc(loginCaptcha.question)}?</label>
      <input class="form-input" id="login-captcha" inputmode="numeric" autocomplete="off" required>
    </div>`;
}

function showLoginCaptcha() {
  const slot = document.getElementById('login-captcha-slot');
  if (!slot) return;
  slot.innerHTML = renderLoginCaptcha();
  const input = slot.querySelector('#login-captcha');
  if (input) input.focus();
}

// ── Forgot-password pane (TPT194) ──
// Two states off `forgotSent`: the request form, then the generic
// confirmation. There is deliberately NO "Resend" button here (unlike the
// signup code step): the server's 60s resend cooldown is invisible to the
// client — a cooled-down request still answers 200 — so a resend button
// would claim to have sent something it silently skipped. "Try a different
// email" (typo recovery) is the honest affordance.
function renderForgot() {
  const handoffNotice = desktopHandoff ? '<p>Signing in to the TipΔTask desktop app</p>' : '';
  if (forgotSent) {
    return `
      ${handoffNotice}
      <p>If an account exists for <strong>${esc(forgotEmail)}</strong>, we’ve sent a link to reset your password. It expires in 60 minutes.</p>
      <p>Nothing there? Check your spam folder, or <a href="#" id="forgot-retry">try a different email</a>. Asked for a link a moment ago? That earlier one still works.</p>
      <div class="login-switch"><a href="#" id="go-signin">Back to sign in</a></div>`;
  }
  return `
    ${handoffNotice}
    <p>Forgot your password? Enter your email and we’ll send you a link to reset it.</p>
    <form class="login-form" id="forgot-form">
      <div class="form-group">
        <label class="form-label" for="forgot-email">Email</label>
        <input class="form-input" id="forgot-email" type="email" autocomplete="email" required autofocus>
      </div>
      <div class="login-error" id="forgot-error"></div>
      <button type="submit" class="btn btn-primary btn-block" id="forgot-btn">Send reset link</button>
    </form>
    <div class="login-switch"><a href="#" id="go-signin">Back to sign in</a></div>`;
}

// ── Signup wizard panes (C1496) ──
// One dispatcher (the name the task requires) + three step renderers, mirroring
// the signin/signup/twofa dispatch in render() above. Only renderSignupWizard()
// is called from outside this block.
function renderSignupWizard() {
  const handoffNotice = desktopHandoff ? '<p>Signing in to the TipΔTask desktop app</p>' : '';
  if (signupStep === 'code') return handoffNotice + renderSignupCodeStep();
  if (signupStep === 'profile') return handoffNotice + renderSignupProfileStep();
  return handoffNotice + renderSignupEmailStep();
}

function renderSignupEmailStep() {
  // TPT96 — betaCode set means this step was reached via a validated
  // registration link (pages/register.js), not the plain "Create account"
  // link — the email input below is still editable/submittable exactly the
  // same either way, this is purely a confirmation line.
  // TPT103 — signupEmail is deliberately blank for a shared (team) code
  // (register.js skips passing the original applicant's email) — showing
  // "confirmed for <the applicant's address>" to a teammate would leak a
  // stranger's email, so the notice only renders when there's a real,
  // specific address to confirm.
  const betaNotice = (betaCode && signupEmail)
    ? `<p>Beta access confirmed for <strong>${esc(signupEmail)}</strong></p>`
    : '';
  return `
    ${betaNotice}
    <p>Create your account</p>
    <form class="login-form" id="signup-email-form">
      <div class="form-group">
        <label class="form-label" for="signup-email">Email</label>
        <input class="form-input" id="signup-email" type="email" autocomplete="email" required autofocus>
      </div>
      <div class="login-error" id="signup-error"></div>
      <button type="submit" class="btn btn-primary btn-block" id="signup-send-btn">Send code</button>
    </form>
    <div class="login-switch">Already have an account? <a href="#" id="go-signin">Sign in</a></div>`;
}

function renderSignupCodeStep() {
  return `
    <p>Step 2 of 3 — enter the 6-digit code sent to <strong>${esc(signupEmail)}</strong></p>
    <form class="login-form" id="signup-code-form">
      <div class="form-group">
        <label class="form-label" for="signup-code">Verification code</label>
        <input class="form-input" id="signup-code" inputmode="numeric" autocomplete="one-time-code" autofocus required>
      </div>
      <div class="login-error" id="signup-error"></div>
      <button type="submit" class="btn btn-primary btn-block" id="signup-verify-btn">Verify</button>
      <button type="button" class="btn btn-ghost btn-sm btn-block" id="signup-resend-btn" style="margin-top:0.5rem;">Resend code</button>
    </form>
    <div class="login-switch"><a href="#" id="signup-change-email">Use a different email</a> · <a href="#" id="go-signin">Sign in</a></div>`;
}

function renderSignupProfileStep() {
  const avatarHtml = renderAvatarHtml(signupAvatar ? { avatar_url: signupAvatar } : null, 96);
  const experienceOptions = EXPERIENCE_OPTIONS
    .map(o => `<option value="${o.value}">${esc(o.label)}</option>`)
    .join('');
  return `
    <p>Step 3 of 3 — complete your profile</p>
    <form class="login-form" id="signup-profile-form">
      <input type="file" id="signup-avatar-input" accept="image/*" hidden>
      <div class="form-group" style="text-align:center;">
        <span id="signup-avatar-preview" class="avatar-drop-zone" style="display:inline-block;cursor:pointer;" title="Add a photo">${avatarHtml}</span>
        <div>
          <button type="button" class="btn btn-ghost btn-sm" id="signup-avatar-remove" style="margin-top:0.5rem;${signupAvatar ? '' : 'display:none;'}">Remove photo</button>
        </div>
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-verified-email">Email</label>
        <input class="form-input" id="signup-verified-email" type="email" autocomplete="username" readonly>
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-name">Name (optional)</label>
        <input class="form-input" id="signup-name" autocomplete="name">
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-password">Password</label>
        <div class="password-field has-toggle">
          <input class="form-input" id="signup-password" type="password" autocomplete="new-password" minlength="8" required>
          ${passwordToggleButton('signup-password-toggle')}
        </div>
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-confirm">Confirm password</label>
        <div class="password-field">
          <input class="form-input" id="signup-confirm" type="password" autocomplete="new-password" minlength="8" required>
        </div>
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-headline">Headline (optional) — e.g. Senior backend engineer</label>
        <input class="form-input" id="signup-headline" maxlength="160">
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-background">Background (optional)</label>
        <textarea class="form-textarea" id="signup-background" maxlength="4000"></textarea>
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-experience">Experience (optional)</label>
        <select class="form-select" id="signup-experience">${experienceOptions}</select>
      </div>
      <div class="login-error" id="signup-error"></div>
      <button type="submit" class="btn btn-primary btn-block" id="signup-complete-btn">Create account</button>
    </form>
    <div class="login-switch">Already have an account? <a href="#" id="go-signin">Sign in</a></div>`;
}

function renderTwofa() {
  return `
    <p>Enter the 6-digit code from your authenticator app, or a backup code${pendingEmail ? ` for <strong>${esc(pendingEmail)}</strong>` : ''}</p>
    <form class="login-form" id="twofa-form">
      <div class="form-group">
        <label class="form-label" for="twofa-code">Code or backup code</label>
        <input class="form-input" id="twofa-code" autocomplete="one-time-code" autofocus required>
      </div>
      <div class="login-error" id="twofa-error"></div>
      <button type="submit" class="btn btn-primary btn-block" id="twofa-btn">Verify</button>
    </form>
    <div class="login-switch"><a href="#" id="go-back">Back</a></div>`;
}

function bind() {
  const app = document.getElementById('app');

  const goSignup = app.querySelector('#go-signup');
  if (goSignup) goSignup.addEventListener('click', (e) => { e.preventDefault(); resetSignupState(); loginCaptcha = null; mode = 'signup'; render(); });

  const goSignin = app.querySelector('#go-signin');
  if (goSignin) goSignin.addEventListener('click', (e) => { e.preventDefault(); resetSignupState(); loginCaptcha = null; mode = 'signin'; render(); });

  const goBack = app.querySelector('#go-back');
  if (goBack) goBack.addEventListener('click', (e) => { e.preventDefault(); tempToken = null; loginCaptcha = null; mode = 'signin'; render(); });

  // ── Account-chooser bindings (TPT347) ──

  const chooseContinue = app.querySelector('#choose-continue');
  if (chooseContinue) {
    chooseContinue.addEventListener('click', async () => {
      const errEl = app.querySelector('#choose-error');
      errEl.textContent = '';
      chooseContinue.disabled = true;
      // Navigates away on success (nothing may run after it); false = no portable token could be
      // minted — say so and leave the switch button working rather than stranding the user.
      if (!(await handoffWithSession())) {
        chooseContinue.disabled = false;
        errEl.textContent = 'Could not continue with this account. Use a different account instead.';
      }
    });
  }

  const chooseSwitch = app.querySelector('#choose-switch');
  if (chooseSwitch) {
    chooseSwitch.addEventListener('click', async () => {
      chooseSwitch.disabled = true;
      // Ending this browser's session is what makes the switch stick: without it a reload of
      // this page would verifySession() successfully and land on the chooser again.
      await signOutSession();
      chooserUser = null;
      mode = 'signin';
      render();
    });
  }

  // ── Forgot-password bindings (TPT194) ──

  const goForgot = app.querySelector('#go-forgot');
  if (goForgot) {
    goForgot.addEventListener('click', (e) => {
      e.preventDefault();
      // Carry a half-typed sign-in address over so the user doesn't retype it.
      forgotEmail = app.querySelector('#signin-email')?.value.trim() || '';
      forgotSent = false;
      loginCaptcha = null;
      mode = 'forgot';
      render();
    });
  }

  const forgotRetry = app.querySelector('#forgot-retry');
  if (forgotRetry) forgotRetry.addEventListener('click', (e) => { e.preventDefault(); forgotSent = false; render(); });

  const forgotForm = app.querySelector('#forgot-form');
  if (forgotForm) {
    const emailInput = app.querySelector('#forgot-email');
    emailInput.value = forgotEmail;
    forgotForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = app.querySelector('#forgot-btn');
      const errEl = app.querySelector('#forgot-error');
      // Same normalization the server applies (trim + lowercase).
      const email = emailInput.value.trim().toLowerCase();
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Sending...';
      try {
        await forgotPassword(email);
        forgotEmail = email;
        forgotSent = true;
        render();
      } catch (err) {
        // Inline, and no render(): re-rendering would wipe the typed address.
        errEl.textContent = forgotPasswordErrorText(err);
        btn.disabled = false;
        btn.textContent = 'Send reset link';
      }
    });
  }

  const signinForm = app.querySelector('#signin-form');
  if (signinForm) {
    signinForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = app.querySelector('#signin-btn');
      const errEl = app.querySelector('#signin-error');
      const email = app.querySelector('#signin-email').value.trim();
      const password = app.querySelector('#signin-password').value;
      const captcha = loginCaptcha
        ? { token: loginCaptcha.token, answer: (app.querySelector('#login-captcha')?.value || '').trim() }
        : undefined;
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Signing in...';
      try {
        const result = await loginWithPassword(email, password, captcha);
        loginCaptcha = null;
        if (result.requires2fa) {
          tempToken = result.tempToken;
          pendingEmail = email;
          mode = 'twofa';
          render();
          return;
        }
        if (desktopHandoff) { completeHandoff(); return; }
        navigate('/');
      } catch (err) {
        // Inline, and no render(): re-rendering would wipe the typed
        // email/password. Only the captcha slot is repainted.
        const challenge = captchaChallengeFromError(err);
        if (challenge) {
          loginCaptcha = challenge;
          showLoginCaptcha();
          errEl.textContent = captcha
            ? (err.message || 'Incorrect answer — try the new question')
            : 'Too many failed attempts — answer the question below to continue.';
        } else {
          if (loginCaptcha) {
            // The server consumed the token before checking the password.
            loginCaptcha = null;
            showLoginCaptcha();
          }
          errEl.textContent = err.message || 'Invalid email or password';
        }
        btn.disabled = false;
        btn.textContent = 'Sign In';
      }
    });
  }

  // ── Signup wizard bindings (C1496) ──

  const signupEmailForm = app.querySelector('#signup-email-form');
  if (signupEmailForm) {
    const emailInput = app.querySelector('#signup-email');
    emailInput.value = signupEmail;
    signupEmailForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = app.querySelector('#signup-send-btn');
      const errEl = app.querySelector('#signup-error');
      // Normalize the same way the server does (auth.js lowercases+trims) so
      // the code pane's displayed address and the resend cooldown key match
      // exactly what was actually sent to.
      const email = emailInput.value.trim().toLowerCase();
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Sending...';
      try {
        await signupStart(email, betaCode);
        signupEmail = email;
        resendReadyAt = Date.now() + 60_000;
        signupStep = 'code';
        render();
      } catch (err) {
        showSignupError(errEl, err, 'start');
        btn.disabled = false;
        btn.textContent = 'Send code';
      }
    });
  }

  const signupCodeForm = app.querySelector('#signup-code-form');
  if (signupCodeForm) {
    signupCodeForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = app.querySelector('#signup-verify-btn');
      const errEl = app.querySelector('#signup-error');
      const code = app.querySelector('#signup-code').value.trim();
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Verifying...';
      try {
        const result = await signupVerify(signupEmail, code);
        // This assignment is the only path to signupStep = 'profile' — the
        // structural guarantee that step 2 is unreachable without a verified
        // code, not just a UI convention.
        signupToken = result.signupToken;
        stopResendTimer();
        signupStep = 'profile';
        render();
      } catch (err) {
        errEl.textContent = signupErrorText(err, 'verify');
        btn.disabled = false;
        btn.textContent = 'Verify';
      }
    });

    // type="button" is load-bearing here — this sits inside <form> and would
    // otherwise submit it (and thus verify with an empty code) on click.
    const resendBtn = app.querySelector('#signup-resend-btn');
    resendBtn.addEventListener('click', async () => {
      const errEl = app.querySelector('#signup-error');
      errEl.textContent = '';
      resendBtn.disabled = true;
      try {
        await signupStart(signupEmail, betaCode);
        resendReadyAt = Date.now() + 60_000;
        startResendTimer();
        showToast('New code sent', 'success');
      } catch (err) {
        // A cooldown 429 here still means "wait ~60s", so reset the same
        // countdown rather than leaving the button permanently disabled with
        // no way to know when it'll re-enable.
        resendReadyAt = Date.now() + 60_000;
        startResendTimer();
        errEl.textContent = signupErrorText(err, 'start');
      }
    });
    startResendTimer(); // resumes the countdown across re-renders of this pane
  }

  const changeEmail = app.querySelector('#signup-change-email');
  if (changeEmail) {
    changeEmail.addEventListener('click', (e) => {
      e.preventDefault();
      // The code row is consumed at verify — there is no going "back" to a
      // still-valid code, only starting over with a (possibly new) address.
      signupToken = null;
      stopResendTimer();
      signupStep = 'email';
      render();
    });
  }

  const signupProfileForm = app.querySelector('#signup-profile-form');
  if (signupProfileForm) {
    const verifiedEmailInput = app.querySelector('#signup-verified-email');
    verifiedEmailInput.value = signupEmail;

    const avatarInput = app.querySelector('#signup-avatar-input');
    const avatarPreview = app.querySelector('#signup-avatar-preview');
    const avatarRemoveBtn = app.querySelector('#signup-avatar-remove');

    bindAvatarPicker({
      preview: avatarPreview,
      input: avatarInput,
      removeBtn: avatarRemoveBtn,
      form: signupProfileForm,
      onPick: (dataUrl) => {
        signupAvatar = dataUrl;
        avatarPreview.innerHTML = renderAvatarHtml({ avatar_url: signupAvatar }, 96);
      },
      onRemove: () => {
        signupAvatar = null;
        avatarPreview.innerHTML = renderAvatarHtml(null, 96);
      },
      onError: (msg) => { app.querySelector('#signup-error').textContent = msg; },
    });

    bindPasswordToggle(signupProfileForm);

    signupProfileForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = app.querySelector('#signup-complete-btn');
      const errEl = app.querySelector('#signup-error');
      const name = app.querySelector('#signup-name').value;
      const password = app.querySelector('#signup-password').value;
      const confirm = app.querySelector('#signup-confirm').value;
      const headline = app.querySelector('#signup-headline').value;
      const background = app.querySelector('#signup-background').value;
      const experienceYears = app.querySelector('#signup-experience').value;
      errEl.textContent = '';
      if (password !== confirm) {
        errEl.textContent = 'Passwords do not match';
        return;
      }
      btn.disabled = true;
      btn.textContent = 'Creating account...';
      try {
        // buildSignupBody() (called inside signupComplete, lib/signup-form.js)
        // trims strings and omits blank optionals — raw values are fine here.
        await signupComplete(signupToken, {
          name, password, avatar: signupAvatar, headline, background,
          experience_years: experienceYears,
        }, betaCode);
        if (desktopHandoff) { completeHandoff(); return; }
        navigate('/');
      } catch (err) {
        if (err.status === 401) {
          // Signup token expired/invalid (30 min TTL, or already spent) — the
          // only fix is to prove the email again. Toast + full restart rather
          // than an inline message, since nothing on this pane can resolve it.
          showToast('Your verification expired — please start again', 'error');
          resetSignupState();
          render();
          return;
        }
        showSignupError(errEl, err, 'complete');
        btn.disabled = false;
        btn.textContent = 'Create account';
      }
    });
  }

  const twofaForm = app.querySelector('#twofa-form');
  if (twofaForm) {
    twofaForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = app.querySelector('#twofa-btn');
      const errEl = app.querySelector('#twofa-error');
      const code = app.querySelector('#twofa-code').value.trim();
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Verifying...';
      try {
        const result = await completeTwoFactorLogin(tempToken, code);
        tempToken = null;
        if (result.backupCodeUsed) {
          showToast(`Signed in with a backup code — ${result.backupCodesRemaining} left`, 'success');
        }
        if (desktopHandoff) { completeHandoff(); return; }
        navigate('/');
      } catch (err) {
        if (err.status === 401 && /expired|session/i.test(err.message || '')) {
          showToast('Session expired — sign in again', 'error');
          tempToken = null;
          mode = 'signin';
          render();
          return;
        }
        errEl.textContent = err.message || 'Invalid code';
        btn.disabled = false;
        btn.textContent = 'Verify';
      }
    });
  }
}

// Hands the browser's live session to the desktop app. Returns false — nothing navigated —
// when no portable Bearer token could be had, so the caller falls back to the sign-in form (or,
// in the chooser, an inline error). A password/2FA/signup-signed-in browser already has one in
// localStorage (getToken()); a cookie-only Google session doesn't, since the cookie alone drives
// the SPA (C1249). For that case, mint one from the live cookie session (GET /auth/session-token,
// C1466) — `_silent` since a mint failure here is not a "session expired" event, just "fall back
// to the form" (see `tt-web-auth.md` § Gotchas). The Google button itself never reaches this
// path — it's routed straight to the loopback server-side (`desktop` branch in
// api/src/routes/auth.js).
async function handoffWithSession() {
  let token = getToken();
  if (!token) {
    try {
      const res = await apiFetch('/auth/session-token', { _silent: true });
      token = res.token;
    } catch { /* fall through to the caller's fallback */ }
  }
  if (!token) return false;
  completeHandoff(token);
  return true;
}

export async function loginPage() {
  // Pick up a desktop handoff either fresh off the URL (the Task App just
  // opened this page) or from a prior page load in the same tab (e.g. the
  // user reloaded mid-flow) — see module comment above.
  const fromHash = readHandoffFromHash(window.location.hash);
  if (fromHash) {
    desktopHandoff = fromHash;
    try { sessionStorage.setItem(HANDOFF_STORAGE_KEY, JSON.stringify(fromHash)); } catch { /* ignore */ }
  } else {
    desktopHandoff = readStoredHandoff();
  }
  chooserUser = null; // re-derived below; never carried over from an earlier visit

  // Landing here can mean two things: a genuinely logged-out visitor, or a
  // browser that just returned from the Google OAuth redirect (JWT already
  // set as an httpOnly cookie, no manual refresh should be needed). Re-check
  // the session silently before painting the login form so the latter case
  // bounces straight into the app.
  if (await verifySession()) {
    // Drop a queued forgot-pane request (TPT194): a signed-in visitor bounces
    // straight past it, and leaving the flag set would open the forgot pane
    // out of nowhere on their next logout → login.
    pendingForgot = false;
    pendingBeta = null; // same reason — a signed-in visit to #/signup or #/register
    if (!desktopHandoff) { navigate('/'); return; }

    if (desktopHandoff.chooseAccount) {
      // Change Account: don't silently hand the existing session back — offer the choice
      // (painted below, after the shared state reset).
      chooserUser = getUser();
    } else if (await handoffWithSession()) {
      return;
    }
  }

  teardownLayout();
  mode = 'signin';
  tempToken = null;
  loginCaptcha = null;
  pendingEmail = '';
  forgotEmail = '';
  forgotSent = false;
  resetSignupState();

  // TPT194 — the reset page's dead-link "Request a new link" lands straight on
  // the forgot pane instead of making the user find the link again.
  if (pendingForgot) {
    mode = 'forgot';
    pendingForgot = false;
  }

  // TPT96 — a validated beta code from pages/register.js takes us straight
  // into the signup wizard's email step, pre-filled. renderSignupEmailStep()'s
  // existing `emailInput.value = signupEmail` line needs no change to pick
  // this up.
  if (pendingBeta) {
    betaCode = pendingBeta.code;
    signupEmail = pendingBeta.email;
    mode = 'signup';
    signupStep = 'email';
    pendingBeta = null;
  }

  // TPT347 — the chooser only replaces the plain sign-in pane; a queued forgot/beta-signup
  // pane (both set above) keeps priority.
  if (chooserUser && mode === 'signin') mode = 'choose';

  render();
}
